import React, { useMemo } from 'react';

import { Navigate, useLocation } from 'react-router-dom';

import { usePermissions } from '../../hooks/usePermissions';
import { useAppSelector } from '../../store/hooks';
import { UserRole } from '../../types';
import { isMarketingHost } from '../../utils/siteHost';
import { LoadingSpinner } from '../common';
import { RedirectToAppHost } from '../routing/RedirectToAppHost';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermissions?: string[];
  fallbackPath?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermissions,
  fallbackPath = '/login',
}) => {
  const { isAuthenticated, user, isLoading } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // Checking only auth now (permissions live in user)
  const isChecking = useMemo(() => isLoading, [isLoading]);

  if (isMarketingHost()) {
    return <RedirectToAppHost />;
  }

  // Show loading spinner while checking authentication/permissions
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return (
      <Navigate
        to={fallbackPath}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role-based access if required
  if (requiredRole && !hasRequiredRole(user.role, requiredRole)) {
    return (
      <Navigate
        to="/unauthorized"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check permission-based access if required
  if (requiredPermissions && requiredPermissions.length > 0) {
    // Permissions are fetched right after login / rehydration; if they haven't
    // arrived yet, wait instead of wrongly redirecting to /unauthorized.
    if (user.permissions === undefined) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    // Require ANY of the listed permissions (user needs at least one)
    const ok = requiredPermissions?.some(p => hasPermission(p));

    if (!ok) {
      return (
        <Navigate
          to="/unauthorized"
          state={{ from: location }}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

// Helper function to check if user has required role
const hasRequiredRole = (userRole: UserRole, requiredRole: UserRole): boolean => {
  const roleHierarchy: Record<UserRole, number> = {
    [UserRole.SUPER_ADMIN]: 5,
    [UserRole.STATE_VIEWER]: 1,
    [UserRole.DISTRICT_ADMIN]: 4,
    [UserRole.SCHOOL_ADMIN]: 3,
    [UserRole.PRINCIPAL]: 3,
    [UserRole.ASSISTANT_PRINCIPAL]: 3,
    [UserRole.EVALUATOR]: 2,
    [UserRole.TEACHER]: 1,
    [UserRole.PARENT]: 0,
  };

  return (roleHierarchy[userRole] || 0) >= (roleHierarchy[requiredRole] || 0);
};
